import React, {useMemo} from 'react';
import type {ResponsiveValue, TextProps} from '@tempo/core';
import {Box, Text, Tooltip} from '@tempo/core';
import {
	ArrowBottomIcon,
	ArrowTopIcon,
	InformationIcon,
	SortIcon,
} from '@tempo/icons';

import type {ThProps} from '../core/Th';
import Th from '../core/Th';
import {useTableContext} from '@components/Table';

type HeaderCellProps = {
	id?: string;
	label: string;
	info?: string;
	textProps?: TextProps;
	sortable?: ResponsiveValue<boolean>;
	'data-testid'?: string;
} & ThProps;

const HeaderCell = ({
	id,
	label,
	info,
	sortable,
	textProps,
	...rest
}: HeaderCellProps) => {
	const {stickyHeader, sort, setSort} = useTableContext();

	const handleSort = () => {
		if (id) {
			setSort(id);
		}
	};

	const sortContent = useMemo(() => {
		if (!sortable) {
			return;
		}

		const IconComponent =
			sort?.key === id
				? sort?.asc
					? ArrowTopIcon
					: ArrowBottomIcon
				: SortIcon;

		return (
			<IconComponent
				className="svg-icon"
				boxSize="icon.s"
				ms={1}
				_groupHover={{display: 'block'}}
				display={!sort || sort.key !== id ? 'none' : undefined}
			/>
		);
	}, [id, sort, sortable]);

	return (
		<Th
			sx={
				stickyHeader
					? {
							borderBottom: '1px solid',
							borderBottomColor: 'divider.neutral.primary.default',
						}
					: {
							borderWidth: 0,
						}
			}
			px={0}
			{...rest}
		>
			<Box
				display="flex"
				justifyContent="space-between"
				alignItems="center"
				pe={info ? 2 : undefined}
			>
				<Text
					color="text.neutral.secondary.default"
					textTransform="none"
					whiteSpace="pre-wrap"
					minH={4}
					{...(sortable
						? {
								display: 'flex',
								alignItems: 'center',
								cursor: 'pointer',
								onClick: handleSort,
								role: 'group',
								_hover: {
									color: 'text.neutral.primary.default',
								},
							}
						: {})}
					{...textProps}
				>
					{label}
					{sortContent}
				</Text>

				{info && (
					<Tooltip hasArrow={true} label={info} placement="auto">
						<InformationIcon
							boxSize="icon.m"
							display="inline-block"
							ms={2}
							cursor="pointer"
						/>
					</Tooltip>
				)}
			</Box>
		</Th>
	);
};

export default HeaderCell;
